import { useMemo, useState } from 'react';

import { IRow, ITableSelectionProps } from 'common/types';

export const useSelection = <T extends { id: string }>(
    items: T[],
    selectFilter?: (item: T) => boolean
): ITableSelectionProps<T> => {
    // A list based on item ids
    const [selected, setSelected] = useState<string[]>([]);

    let selectableItems = items;

    // If a selectFilter is given we filter the items, so we have a list of selectable items
    if (selectFilter) {
        selectableItems = selectableItems.filter(selectFilter);
    }

    // items/selected are new array references on every render (e.g. a fresh
    // GraphQL response), so key the memo on their serialized value rather
    // than identity.
    const itemsKey = JSON.stringify(items);
    const selectedKey = JSON.stringify(selected);

    const rows = useMemo<IRow[]>(
        () =>
            items.map((item, index) => {
                const isSelected = selected.indexOf(item.id) > -1;

                return {
                    index,
                    isSelected,
                    onSelect: () => {
                        if (isSelected) {
                            return setSelected(
                                selected.filter(
                                    (selectedItemId) =>
                                        item.id !== selectedItemId
                                )
                            );
                        }

                        setSelected([...selected, item.id]);
                    },
                };
            }),
        [itemsKey, selectedKey]
    );

    const isSelected = selected.length > 0;
    const isSelectedAll =
        !!selectableItems.length && selected.length === selectableItems.length;

    return {
        rows,
        selected,
        isSelected,
        isSelectedAll,
        selectableItems,
        setSelected,
        onSelectAll: () => {
            if (!isSelected) {
                return setSelected(
                    selectableItems.map((selectableItem) => selectableItem.id)
                );
            }

            setSelected([]);
        },
    };
};
